import axios, { AxiosError, AxiosResponse } from "axios";
import { API_LINKS } from "../constants/api";

export const QUERY_PAGE_SIZE = 50;

const apimSubscriptionKey = import.meta.env.OCP_APIM_SUBSCRIPTION_KEY;

export const defaultApiUrl = import.meta.env.VITE_BACKEND_ENDPOINT;

let isAuthRedirectInProgress = false;

declare module "axios" {
  export interface AxiosRequestConfig {
    skipAuthRedirect?: boolean;
  }
}

export class ApiRequestError extends Error {
  readonly status?: number;
  readonly data?: unknown;

  constructor(message: string, status?: number, data?: unknown) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.data = data;
  }
}

function redirectToSessionExpiredLogout() {
  if (isAuthRedirectInProgress) return;
  isAuthRedirectInProgress = true;
  window.location.href = API_LINKS.logout + "?messageId=18";
}

export function isUnauthorizedError(error: unknown): boolean {
  if (error instanceof ApiRequestError) return error.status === 401;
  if (axios.isAxiosError(error)) return error.response?.status === 401;
  return false;
}

export function isNotFoundError(error: unknown): boolean {
  if (error instanceof ApiRequestError)
    return error.status === 404 || error.status === 410;
  if (axios.isAxiosError(error))
    return error.response?.status === 404 || error.response?.status === 410;
  return false;
}

function resolveApiErrorMessage(error: unknown): string {
  if (!axios.isAxiosError(error)) {
    return error instanceof Error
      ? error.message
      : "Der opstod en fejl. Prøv igen senere.";
  }

  const data = error.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (data && typeof data === "object" && "title" in data && data.title) {
    return String(data.title);
  }
  if (data && typeof data === "object" && "message" in data && data.message) {
    return String(data.message);
  }
  if (data && typeof data === "object" && "errors" in data) {
    const errors = (data as { errors: unknown }).errors;
    if (Array.isArray(errors)) return errors.join(", ");
    if (typeof errors === "object" && errors !== null) {
      return Object.values(errors as Record<string, string[]>)
        .flat()
        .join(", ");
    }
  }
  if (error.response?.status === 400) {
    return "Ugyldige oplysninger. Ret felterne og prøv igen.";
  }
  return "Der opstod en fejl. Prøv igen senere.";
}

function normalizeAxiosError(error: unknown): ApiRequestError {
  if (error instanceof ApiRequestError) return error;
  if (axios.isAxiosError(error)) {
    return new ApiRequestError(
      resolveApiErrorMessage(error),
      error.response?.status,
      error.response?.data,
    );
  }
  return new ApiRequestError(
    error instanceof Error
      ? error.message
      : "Der opstod en fejl. Prøv igen senere.",
  );
}

export const apiClient = axios.create({
  headers: {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
    ...(apimSubscriptionKey && {
      "Ocp-Apim-Subscription-Key": apimSubscriptionKey,
    }),
  },
  withCredentials: true,
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (!error.config?.skipAuthRedirect && error.response?.status === 401) {
      redirectToSessionExpiredLogout();
    }
    return Promise.reject(normalizeAxiosError(error));
  },
);

function buildUrl(apiPrefix: string, query: string): string {
  return `${apiPrefix}/${query}`;
}

async function requestData<T>(fn: () => Promise<AxiosResponse<T>>): Promise<T> {
  const response = await fn();
  return response.data;
}

export async function get<T>({
  query = "",
  apiPrefix = defaultApiUrl,
  skipAuthRedirect = false,
}): Promise<T> {
  return requestData(() =>
    apiClient.get<T>(buildUrl(apiPrefix, query), {
      ...(skipAuthRedirect ? { skipAuthRedirect: true } : {}),
    }),
  );
}

export async function getWithErrorObject<T>({
  query = "",
  apiPrefix = defaultApiUrl,
}): Promise<T> {
  return requestData(() => apiClient.get<T>(buildUrl(apiPrefix, query)));
}

export async function getAnonymous<T>({
  query = "",
  apiPrefix = defaultApiUrl,
}): Promise<T> {
  return requestData(() =>
    apiClient.get<T>(buildUrl(apiPrefix, query), { skipAuthRedirect: true }),
  );
}

export async function getResponse<T>({
  query = "",
  apiPrefix = defaultApiUrl,
}): Promise<AxiosResponse<T>> {
  return apiClient.get<T>(buildUrl(apiPrefix, query));
}

export async function put<T>({
  query = "",
  body = {},
  apiPrefix = defaultApiUrl,
}): Promise<T> {
  return requestData(() => apiClient.put<T>(buildUrl(apiPrefix, query), body));
}

export async function post<T>({
  query = "",
  body = {},
  apiPrefix = defaultApiUrl,
}): Promise<T> {
  return requestData(() => apiClient.post<T>(buildUrl(apiPrefix, query), body));
}

export async function postFormWithoutAuthRedirect<T>({
  query = "",
  body = {},
  apiPrefix = defaultApiUrl,
}): Promise<T> {
  try {
    const response = await axios.postForm<T>(buildUrl(apiPrefix, query), body, {
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        ...(apimSubscriptionKey && {
          "Ocp-Apim-Subscription-Key": apimSubscriptionKey,
        }),
      },
      withCredentials: true,
    });
    return response.data;
  } catch (error: unknown) {
    throw normalizeAxiosError(error);
  }
}

export async function postForm<T>({
  query = "",
  body = {},
  apiPrefix = defaultApiUrl,
  signal,
  timeoutMs,
}: {
  query?: string;
  body?: unknown;
  apiPrefix?: string;
  signal?: AbortSignal;
  timeoutMs?: number;
}): Promise<T> {
  return requestData(() =>
    apiClient.postForm<T>(buildUrl(apiPrefix, query), body, {
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        ...(apimSubscriptionKey && {
          "Ocp-Apim-Subscription-Key": apimSubscriptionKey,
        }),
      },
      ...(signal ? { signal } : {}),
      ...(timeoutMs ? { timeout: timeoutMs } : {}),
    }),
  );
}

export async function getBlob({
  query = "",
  apiPrefix = defaultApiUrl,
}): Promise<Blob> {
  return requestData(() =>
    apiClient.get<Blob>(buildUrl(apiPrefix, query), {
      responseType: "blob",
    }),
  );
}

export async function patch<T>({
  query = "",
  body = {},
  apiPrefix = defaultApiUrl,
}): Promise<T> {
  return requestData(() =>
    apiClient.patch<T>(buildUrl(apiPrefix, query), body, {
      headers: {
        "Content-Type": "application/json-patch+json",
      },
    }),
  );
}

export async function patchJson<T>({
  query = "",
  body = {},
  apiPrefix = defaultApiUrl,
}): Promise<T> {
  return requestData(() =>
    apiClient.patch<T>(buildUrl(apiPrefix, query), body),
  );
}

export async function patchJsonWithoutAuthRedirect<T>({
  query = "",
  body = {},
  apiPrefix = defaultApiUrl,
}): Promise<T> {
  try {
    const response = await axios.patch<T>(buildUrl(apiPrefix, query), body, {
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        ...(apimSubscriptionKey && {
          "Ocp-Apim-Subscription-Key": apimSubscriptionKey,
        }),
      },
      withCredentials: true,
    });
    return response.data;
  } catch (error: unknown) {
    throw normalizeAxiosError(error);
  }
}

export async function putForm<T>({
  query = "",
  body = {},
  apiPrefix = defaultApiUrl,
}): Promise<T> {
  return requestData(() =>
    apiClient.putForm<T>(buildUrl(apiPrefix, query), body, {
      headers: {
        "Content-Type": "application/json-put+json",
      },
    }),
  );
}

export async function del<T>({
  query = "",
  apiPrefix = defaultApiUrl,
}): Promise<AxiosResponse<T>> {
  return apiClient.delete<T>(buildUrl(apiPrefix, query));
}

const apiUtil = {
  get,
  put,
  post,
  patch,
  del,
};
export default apiUtil;
